/*!
 * feedback.js — a delay's loop: fed back through a tone and a saturator
 * © 2026 Rob Canning — GPLv3
 *
 *     delay:{time:0.25, fb:0.6, mix:0.3}             echoes, as before
 *     delay:{time:0.125, fb:1.1, tone:1500, sat:0.5}  past 1: it runs away into a held squeal, darkening
 *
 *   fb    0..1.5   how much of each echo comes round again; over 1 each is
 *                  louder than the last -- until the saturator holds it
 *   tone  Hz       a lowpass in the loop: each time round darker (none by default)
 *   sat   0..1     the saturator, tanh(k x) / k with k 1..10: as it is while
 *                  quiet, held under 1/k when loud -- more sat, lower and dirtier
 *
 * The loop: line -> tone -> saturator -> fb -> line. With no tone and sat:0
 * a quiet echo goes round as it did (tanh(x) is x while x is small).
 */

export const FB_MAX = 1.5;

/** The saturator's curve: tanh(k x) / k over -1..1 (beyond: held at the ends, as a WaveShaper does). */
export function satCurve(sat = 0, n = 2049) {
  const k = 1 + 9 * Math.min(1, Math.max(0, Number(sat) || 0));
  const c = new Float32Array(n);
  // over -4..4 of the input, scaled into the curve's -1..1, so a loud echo is held, not clipped flat
  for (let i = 0; i < n; i++) { const x = ((i / (n - 1)) * 2 - 1) * 4; c[i] = Math.tanh(k * x) / k; }
  return c;
}

/**
 * The loop on `line` (a DelayNode): { fbGain, tone, shaper, set(cfg) }. The
 * shaper reads its input over -4..4: a gain of 1/4 before it, and the curve
 * is of x*4 -- so it is the sound as it is below about 0.3.
 */
export function feedbackLoop(ctx, line, cfg = {}) {
  const tone = ctx.createBiquadFilter();
  tone.type = "lowpass";
  tone.Q.value = 0.5;
  const into = ctx.createGain();
  into.gain.value = 0.25;
  const shaper = ctx.createWaveShaper();
  const fbGain = ctx.createGain();
  line.connect(tone).connect(into).connect(shaper).connect(fbGain).connect(line);
  const loop = {
    fbGain, tone, shaper,
    set(c = {}, at = null) {
      const fb = c.fb ?? c.feedback;
      if (fb != null) {
        const v = Math.min(FB_MAX, Math.max(0, Number(fb) || 0));
        if (at == null) fbGain.gain.value = v; else fbGain.gain.setTargetAtTime(v, at, 0.02);
      }
      if (c.tone != null || at == null) {
        const hz = c.tone != null ? Math.min(20000, Math.max(40, Number(c.tone) || 20000)) : 20000;
        if (at == null) tone.frequency.value = hz; else tone.frequency.setTargetAtTime(hz, at, 0.02);
      }
      if (c.sat != null || at == null) shaper.curve = satCurve(c.sat ?? 0);
    },
  };
  loop.set({ fb: 0.25, ...cfg });
  return loop;
}
